import { EVerticalNames } from '@constants';
import { IUser, IUserInfo } from '@sdk/contracts';
import { Divider, Flex, Form, FormInstance } from 'antd';
import { Fragment, useImperativeHandle, forwardRef } from 'react';

import { FpFormContent } from '../FpFormContent';
import { LoadingCircleSpinner } from '../Loaders';
import { OposFormContent } from '../OposFormContent';
import { PceFormContent } from '../PceFormContent';
import { UniFormContent } from '../UniFormContent';
import { WkcFormContent } from '../WkcFormContent';

import { useProfile, useUserInfoByUser } from '@/store';

type Props = {
  data?: IUser;
  form: FormInstance<IUser>;
};

export interface StudentTrainingInfoFormRef {
  getUserInfoValues: () => IUserInfo | null;
  isUserInfoTouched: () => boolean;
}

export const StudentTrainingInfoForm = forwardRef<StudentTrainingInfoFormRef, Props>(
  ({ data, form }, ref) => {
    const [userInfoForm] = Form.useForm<IUserInfo>();
    const {
      roleCheck: { isManager }
    } = useProfile();

    const { userInfo: studentUserInfo, isPending } = useUserInfoByUser(data?._id || '');
    useImperativeHandle(ref, () => ({
      getUserInfoValues: () => {
        if (!studentUserInfo) return null;
        const values = userInfoForm.getFieldsValue();
        return values;
      },
      isUserInfoTouched: () => {
        const touched = userInfoForm.isFieldsTouched();
        return touched;
      }
    }));

    if (isPending) {
      return (
        <Flex justify="center" align="center">
          <LoadingCircleSpinner />
        </Flex>
      );
    }

    if (!data || !studentUserInfo) return null;

    const { tags } = data;

    const verticalsToShow = [...new Set(tags.map(tag => tag.vertical))];
    const isPCE = tags.some(tag => tag.name.includes('PCE'));
    const isAllPCE = tags.every(tag => tag.name.includes('PCE'));

    const FORMS_BY_VERTICAL: Record<EVerticalNames, JSX.Element> = {
      [EVerticalNames.UNI]: (
        <>
          {!isAllPCE && <UniFormContent isManager={isManager} form={userInfoForm} profile={data} />}
          {isPCE && <PceFormContent isManager={isManager} form={userInfoForm} profile={data} />}
        </>
      ),
      [EVerticalNames.OPOS]: (
        <OposFormContent isManager={isManager} form={userInfoForm} profile={data} />
      ),
      [EVerticalNames.FP]: <FpFormContent isManager={isManager} profile={data} />,
      [EVerticalNames.WKC]: <WkcFormContent form={form} />
    };

    return (
      <Form<IUserInfo> layout="vertical" form={userInfoForm} initialValues={studentUserInfo}>
        {verticalsToShow.map((vertical, index) => (
          <Fragment key={vertical}>
            {index > 0 && <Divider />}
            {FORMS_BY_VERTICAL[vertical]}
          </Fragment>
        ))}
      </Form>
    );
  }
);

StudentTrainingInfoForm.displayName = 'StudentTrainingInfoForm';
